/**
 * Cova Chat — branded IDs + closed enums.
 *
 * All IDs are UUIDs. Owner/participant identity is polymorphic (a mailbox
 * addresses a user / underwriter / org_team / client), so the closed sets that
 * are genuinely fixed by the model — `MailboxOwnerType`, `ConversationKind` —
 * are `S.Literal`s here. Everything a USER composes (none in v1) would live in
 * a table, per user-empowerment.md.
 */
import { Schema as S } from "effect"

// ============================================================================
// Branded IDs
// ============================================================================

export const MailboxId = S.UUID.pipe(
  S.brand("MailboxId"),
  S.annotations({
    identifier: "MailboxId",
    title: "Mailbox ID",
    description: "UUID of a chat mailbox",
  }),
)
export type MailboxId = typeof MailboxId.Type

export const ConversationId = S.UUID.pipe(
  S.brand("ConversationId"),
  S.annotations({
    identifier: "ConversationId",
    title: "Conversation ID",
    description: "UUID of a chat conversation",
  }),
)
export type ConversationId = typeof ConversationId.Type

export const ChatMessageId = S.UUID.pipe(
  S.brand("ChatMessageId"),
  S.annotations({
    identifier: "ChatMessageId",
    title: "Chat Message ID",
    description: "UUID of a chat message",
  }),
)
export type ChatMessageId = typeof ChatMessageId.Type

export const ChatParticipantId = S.UUID.pipe(
  S.brand("ChatParticipantId"),
  S.annotations({
    identifier: "ChatParticipantId",
    title: "Chat Participant ID",
    description: "UUID of a chat conversation participant",
  }),
)
export type ChatParticipantId = typeof ChatParticipantId.Type

export const ChatAttachmentId = S.UUID.pipe(
  S.brand("ChatAttachmentId"),
  S.annotations({
    identifier: "ChatAttachmentId",
    title: "Chat Attachment ID",
    description: "UUID of a chat message attachment",
  }),
)
export type ChatAttachmentId = typeof ChatAttachmentId.Type

export const ChatResponseEventId = S.UUID.pipe(
  S.brand("ChatResponseEventId"),
  S.annotations({
    identifier: "ChatResponseEventId",
    title: "Chat Response Event ID",
    description: "UUID of an append-only chat responsiveness event",
  }),
)
export type ChatResponseEventId = typeof ChatResponseEventId.Type

export const MentionId = S.UUID.pipe(
  S.brand("MentionId"),
  S.annotations({
    identifier: "MentionId",
    title: "Mention ID",
    description: "UUID of a chat message mention",
  }),
)
export type MentionId = typeof MentionId.Type

export const CallId = S.UUID.pipe(
  S.brand("CallId"),
  S.annotations({
    identifier: "CallId",
    title: "Call ID",
    description: "UUID of a chat audio call",
  }),
)
export type CallId = typeof CallId.Type

export const CallInviteId = S.UUID.pipe(
  S.brand("CallInviteId"),
  S.annotations({
    identifier: "CallInviteId",
    title: "Call Invite ID",
    description: "UUID of a chat call invite",
  }),
)
export type CallInviteId = typeof CallInviteId.Type

/** Lifecycle of a mid-call invite: the pending row authorizes joinCall;
 *  accepted once the invitee joins; expired when the call goes terminal. */
export const CallInviteStatus = S.Literal("pending", "accepted", "expired")
export type CallInviteStatus = typeof CallInviteStatus.Type

export const CallRecordingId = S.UUID.pipe(
  S.brand("CallRecordingId"),
  S.annotations({
    identifier: "CallRecordingId",
    title: "Call Recording ID",
    description: "UUID of a chat call recording",
  }),
)
export type CallRecordingId = typeof CallRecordingId.Type

// ============================================================================
// Closed enums
// ============================================================================

/** Polymorphic mailbox owner. `owner_id` points at the matching table's UUID. */
export const MailboxOwnerType = S.Literal("user", "underwriter", "org_team", "client")
export type MailboxOwnerType = typeof MailboxOwnerType.Type

/** Thread kind. v1 ships `broker_underwriter`; the rest are modelled for reuse.
 *  `group` is a user-created multi-party thread (no marketplace rules);
 *  `org_channel` is the system-provisioned org-wide multi-party channel. */
export const ConversationKind = S.Literal(
  "broker_underwriter",
  "broker_team",
  "underwriter_team",
  "broker_client",
  "group",
  "org_channel",
)
export type ConversationKind = typeof ConversationKind.Type

/** Multi-party kinds: no single counterparty, no marketplace responsiveness. */
export const MULTI_PARTY_KINDS = ["group", "org_channel"] as const
export const isMultiPartyKind = (kind: string): boolean =>
  (MULTI_PARTY_KINDS as readonly string[]).includes(kind)

/** External-org direct kinds: the 1:1 counterparty sits OUTSIDE the caller's org
 *  — an underwriter or a client — as opposed to a same-org teammate DM
 *  (`broker_team` / `underwriter_team`). Symmetric: `broker_underwriter` is
 *  cross-org for BOTH sides. Drives the inbox "External" indicator. */
export const EXTERNAL_DIRECT_KINDS = ["broker_underwriter", "broker_client"] as const
export const isExternalKind = (kind: string): boolean =>
  (EXTERNAL_DIRECT_KINDS as readonly string[]).includes(kind)

/** Per-participant role within a conversation (1:1 = initiator + recipient;
 *  groups add `owner`/`admin` for management authz). */
export const ChatParticipantRole = S.Literal(
  "initiator",
  "recipient",
  "member",
  "owner",
  "admin",
)
export type ChatParticipantRole = typeof ChatParticipantRole.Type

/** Call lifecycle. Genuinely closed (like HTTP codes), so a fixed `S.Literal`.
 *  `missed` = callee never answered; `declined` = callee rejected; `failed` =
 *  connection error. Signaling (SDP/ICE) lives off-DB on the Durable Object. */
export const CallStatus = S.Literal(
  "ringing",
  "connected",
  "ended",
  "declined",
  "missed",
  "failed",
)
export type CallStatus = typeof CallStatus.Type

/** Opt-in recording lifecycle: client captures → uploads → server transcribes. */
export const CallRecordingStatus = S.Literal(
  "recording",
  "uploading",
  "uploaded",
  "transcribing",
  "transcribed",
  "failed",
)
export type CallRecordingStatus = typeof CallRecordingStatus.Type

// ─── Space meetings (AWS Chime SDK) ──────────────────────────────────────────
// Spaces (group/org_channel) get MEETINGS people join; 1:1 threads get CALLS.
// Media + recording are AWS Chime — no signaling/TURN of our own.

export const MeetingId = S.UUID.pipe(
  S.brand("MeetingId"),
  S.annotations({
    identifier: "MeetingId",
    title: "Meeting ID",
    description: "UUID of a space meeting",
  }),
)
export type MeetingId = typeof MeetingId.Type

export const MeetingTranscriptSegmentId = S.UUID.pipe(
  S.brand("MeetingTranscriptSegmentId"),
  S.annotations({
    identifier: "MeetingTranscriptSegmentId",
    title: "Meeting Transcript Segment ID",
    description: "UUID of one finalised transcript line in a space meeting",
  }),
)
export type MeetingTranscriptSegmentId = typeof MeetingTranscriptSegmentId.Type

/** Meeting lifecycle. Genuinely closed, so a fixed `S.Literal`. `scheduled` →
 *  `live` on first start (CAS, single winner) → `ended`; `cancelled` is terminal
 *  from `scheduled`. An instant meeting is created already heading for `live`. */
export const MeetingStatus = S.Literal("scheduled", "live", "ended", "cancelled")
export type MeetingStatus = typeof MeetingStatus.Type

/** Who a participant is in the room. `host` = created/started it. */
export const MeetingRole = S.Literal("host", "attendee")
export type MeetingRole = typeof MeetingRole.Type

export const MeetingInviteId = S.UUID.pipe(
  S.brand("MeetingInviteId"),
  S.annotations({
    identifier: "MeetingInviteId",
    title: "Meeting Invite ID",
    description: "UUID of a scheduled-meeting invitation",
  }),
)
export type MeetingInviteId = typeof MeetingInviteId.Type

/** INFORMATIONAL in v1 (scheduling spec §3c): invitations are listed but no
 *  behaviour reads this. Present so RSVP can land later without a migration, and
 *  so the .ics can honestly publish PARTSTAT=NEEDS-ACTION. */
export const MeetingInviteResponse = S.Literal("needs_action", "accepted", "declined")
export type MeetingInviteResponse = typeof MeetingInviteResponse.Type

/**
 * What the scheduling UI can emit. Deliberately NOT full RFC-5545: four presets
 * plus one-off. The RRULE these map to is built in exactly one place
 * (`utils/recurrence.ts`) so the stored rule and the emitted .ics can never
 * disagree — note "fortnightly" is not an RFC frequency, it is
 * FREQ=WEEKLY;INTERVAL=2.
 */
export const RecurrencePreset = S.Literal("none", "daily", "weekly", "fortnightly", "monthly")
export type RecurrencePreset = typeof RecurrencePreset.Type
